import { parseUserStrategyRef } from "@trader/shared/StrategiesApi"
import { useSyncExternalStore } from "react"
import { draftKeyOf, NEW_DRAFT } from "./draftDocument.ts"

// Whether the strategy editor is open, and on which draft: "new" (the one
// unsaved strategy) or a saved strategy's id. Tiny and Monaco-free: the tester
// and the strategy list open the editor through it, and the editor's chunk
// loads only once it is open. Remembered in this browser across reloads.

export interface EditorPanelState {
  readonly open: boolean
  /** The draft key: NEW_DRAFT or a strategy id. */
  readonly draft: string
  /** Increases on every open request, so the editor can react to a repeat one (e.g. "New strategy" again). */
  readonly request: number
  /** Start from the template picker (a new strategy). */
  readonly pickTemplate: boolean
  /** Whose editor this is: another user signing in starts closed. */
  readonly user: number | null
}

const STORAGE_KEY = "trader.strategyEditor.v1"

const load = (): EditorPanelState => {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null")
    if (typeof stored === "object" && stored !== null && "open" in stored && "draft" in stored && "user" in stored) {
      const { draft, open, user } = stored
      if (
        typeof open === "boolean" && typeof draft === "string" && typeof user === "number" &&
        (draft === NEW_DRAFT || draftKeyOf(`draft:${draft}`) !== null)
      ) {
        return { open, draft, request: 0, pickTemplate: false, user }
      }
    }
  } catch {
    // Unreadable: start closed.
  }
  return { open: false, draft: NEW_DRAFT, request: 0, pickTemplate: false, user: null }
}

let state = load()
const listeners = new Set<() => void>()

const set = (next: EditorPanelState) => {
  state = next
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ open: next.open, draft: next.draft, user: next.user }))
  } catch {
    // Not remembered; the editor still works.
  }
  for (const listener of listeners) listener()
}

/** The editor's draft key for a pane's strategy id, when it is a user strategy or a draft. */
export const editorDraftFor = (strategyId: string | null | undefined): string | null => {
  if (strategyId == null) return null
  return draftKeyOf(strategyId) ?? parseUserStrategyRef(strategyId)?.strategyId ?? null
}

export const strategyEditor = {
  /** The signed-in user: the editor remembered for someone else starts closed, on a new strategy. */
  signedIn: (user: number): void => {
    if (state.user !== user) set({ open: false, draft: NEW_DRAFT, request: state.request + 1, pickTemplate: false, user })
  },
  /** Opens the editor on a saved strategy's draft. */
  edit: (draft: string): void => set({ ...state, open: true, draft, request: state.request + 1, pickTemplate: false }),
  /** Opens the editor on the new (unsaved) strategy, with the template picker. */
  create: (): void => set({ ...state, open: true, draft: NEW_DRAFT, request: state.request + 1, pickTemplate: true }),
  /** The draft was saved as a strategy: the editor stays on it under its id. */
  rekey: (draft: string): void => set({ ...state, draft }),
  close: (): void => set({ ...state, open: false, pickTemplate: false }),
  get: (): EditorPanelState => state
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const useStrategyEditorPanel = (): EditorPanelState => useSyncExternalStore(subscribe, () => state)
